<template>
<div class="contents_855">
  <header-bar active="wash-request-currents"></header-bar>

  <h1 class="title2">{{ $t('washRequestDeliveryBoxChange.inProgressWashRequest') }}</h1>

  <div class="contents_box" v-show="currentStep === step.EDIT">
    <p class="title3">{{ $t('washRequestDeliveryBoxChange.deliveryBoxPinChange') }}</p>
    <table class="form_tab form_line_top">
      <tr>
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestDeliveryBoxChange.before') }}</p>
        </th>
        <td class="form_line_r">
          <div class="pin-cell-content">
            <span class="pin-cell-text">{{ originalDeliveryBoxPw }}</span>
          </div>
        </td>
      </tr>
      <tr>
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestDeliveryBoxChange.after') }}</p>
        </th>
        <td class="form_line_r">
          <div class="pin-cell-content">
            <input
              type="text"
              v-model="editedDeliveryBoxPw"
              class="form_basic"
              maxlength="255"
              :disabled="!canChangeDeliveryBoxPw"
            >
          </div>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <span class="font_orange" v-show="showWithdrawnError">
        {{ $t('accountWithdrawn') }}
      </span>
      <span class="font_orange" v-for="serverError in serverErrors" :key="serverError">
        {{ serverError }}
      </span>
    </div>

    <p class="submit_bx_l">
      <router-link :to="{ name: 'wash-request-currents' }" class="submit1 bg_gray">
        {{ $t('backButton') }}
      </router-link>
    </p>
    <p class="submit_bx_r">
      <button class="submit1 bg_green confirm-button" :disabled="isConfirmDisabled" @click="goToConfirm">
        {{ $t('confirmButton') }}
      </button>
    </p>
  </div>

  <div class="contents_box" v-show="currentStep === step.CONFIRM">
    <p class="mypage_notes mg30 mg_bt30">
      <span class="font_orange">
        {{ $t('washRequestDeliveryBoxChange.confirmMessage') }}
      </span>
    </p>

    <p class="title3">{{ $t('washRequestDeliveryBoxChange.deliveryBoxPinChange') }}</p>
    <table class="form_tab form_line_top">
      <tr>
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestDeliveryBoxChange.before') }}</p>
        </th>
        <td class="form_line_r">
          <div class="pin-cell-content">
            <span class="pin-cell-text">{{ originalDeliveryBoxPw }}</span>
          </div>
        </td>
      </tr>
      <tr>
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestDeliveryBoxChange.after') }}</p>
        </th>
        <td class="form_line_r">
          <div class="pin-cell-content">
            <span class="pin-cell-text">{{ editedDeliveryBoxPw }}</span>
          </div>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <span class="font_orange" v-show="showWithdrawnError">
        {{ $t('accountWithdrawn') }}
      </span>
      <span class="font_orange" v-for="serverError in serverErrors" :key="serverError">
        {{ serverError }}
      </span>
    </div>

    <p class="submit_bx_l">
      <button class="submit1 bg_gray" @click="backToEdit">
        {{ $t('backButton') }}
      </button>
    </p>
    <p class="submit_bx_r">
      <button class="submit1 bg_green" :disabled="isSubmitting" @click="submit">
        {{ $t('okButton') }}
      </button>
    </p>
  </div>

  <div class="contents_box" v-show="currentStep === step.COMPLETE">
    <p class="title3">{{ $t('washRequestDeliveryBoxChange.deliveryBoxPinChange') }}</p>
    <table class="form_tab pin-complete-table">
      <tr class="pin-row-complete">
        <td class="pin-complete-cell" colspan="2">
          <p class="complete-message">{{ $t('washRequestDeliveryBoxChange.completeMessage') }}</p>
        </td>
      </tr>
    </table>

    <p class="submit_bx1">
      <router-link :to="{ name: 'wash-request-currents' }" class="submit1 bg_green">
        {{ $t('washRequestDeliveryBoxChange.backToCurrents') }}
      </router-link>
    </p>
  </div>
</div>
</template>

<script>
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref
} from 'vue'
import washRequestMixin from '@/mixin/washRequestMixin'
import userApi from '@/api/user'
import {
  useWashRequestFieldChange,
  WASH_REQUEST_FIELD_CHANGE_STEP,
} from '@/composables/useWashRequestFieldChange'

export default defineComponent({
  name: 'wash-request-delivery-box-change',
  mixins: [washRequestMixin],
  props: {
    id: {
      type: [String, Number],
      default: -1,
    },
  },
  setup(props) {
    const vue = getCurrentInstance().proxy
    const originalDeliveryBoxPw = ref('')
    const editedDeliveryBoxPw = ref('')

    const canChangeDeliveryBoxPw = computed(() =>
      !!(currentWashRequest.value && currentWashRequest.value.canChangeDeliveryBoxPw))
    const isConfirmDisabled = computed(() =>
      !canChangeDeliveryBoxPw.value || editedDeliveryBoxPw.value === originalDeliveryBoxPw.value)

    const {
      isSubmitting,
      currentStep,
      currentWashRequest,
      showWithdrawnError,
      serverErrors,
      fetchCurrentWashRequest,
      goToConfirm,
      backToEdit,
      submit,
    } = useWashRequestFieldChange({
      getWashRequestId: () => props.id,
      convertWashRequest: (data) => vue.convWashReq_(data, 0, new Date()),
      applyFetchedWashRequest: (convertedWashRequest) => {
        originalDeliveryBoxPw.value = convertedWashRequest.delivery_box_pw || ''
        editedDeliveryBoxPw.value = originalDeliveryBoxPw.value
      },
      callUpdateApi: () => userApi.updateWashRequestDeliveryBox(currentWashRequest.value.id, {
        delivery_box_pw: editedDeliveryBoxPw.value === '' ? null : editedDeliveryBoxPw.value,
      }),
      isConfirmDisabled: () => isConfirmDisabled.value,
    })

    onMounted(fetchCurrentWashRequest)

    return {
      step: WASH_REQUEST_FIELD_CHANGE_STEP,
      isSubmitting,
      currentStep,
      showWithdrawnError,
      serverErrors,
      originalDeliveryBoxPw,
      editedDeliveryBoxPw,
      canChangeDeliveryBoxPw,
      isConfirmDisabled,
      goToConfirm,
      backToEdit,
      submit,
    }
  },
})
</script>

<style lang="scss" scoped>
.err-msg-area {
  margin: 30px auto 0;
  text-align: center;
  width: 90%;
}

.submit_bx1,
.submit_bx_l,
.submit_bx_r {
  margin: 12px auto;
}

.confirm-button:disabled {
  background-color: #8fd3a9;
  color: #ffffff;
  opacity: 0.8;
  cursor: default;
}

.pin-cell-content {
  display: flex;
  align-items: center;
  min-height: 36px;
}

.form_tab th.form_line_l,
.form_tab td.form_line_r {
  vertical-align: middle;
}

.form_tab th.form_line_l {
  padding: 15px 0;
}

.form_tab td.form_line_r {
  padding: 15px 0;
}

.form_tab th.form_line_l .item2 {
  margin: 0;
}

.pin-cell-text {
  display: inline-block;
  line-height: 36px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .form_tab th.form_line_l {
    padding: 10px 0;
  }

  .form_tab td.form_line_r {
    padding: 8px 0;
  }

  .pin-cell-content {
    min-height: auto;
  }
}

.pin-row-complete .complete-message {
  margin: 0;
  line-height: 36px;
}

.pin-complete-table {
  border-top: 0 !important;
}

.pin-complete-table td.pin-complete-cell {
  border-bottom: 0;
}
</style>
